import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useApiHandler } from '../hooks/useApiHandler';
import { useFormHandler } from '../hooks/useFormHandler';
import { API_STATUS_CODES } from '../../../../constants/common.constants';
import { IFilterOptions, IuseAdvanceSearchProps } from '../constants/ticketlist.interface';
import { DEFAULT_PAYLOAD, customFieldTypes, pendingReport } from '../constants/ticketlist.constants';
import { handleChange, highlightFields, parseQueryString, setTicketsSessionData } from '../constants/ticketlist.utils';

export const useAdvanceSearch = (props: IuseAdvanceSearchProps) => {
    const { pageSortInfo } = props;
    const { advanceSearchForm } = useFormHandler();
    const [resetTrigger, setResetTrigger] = useState(false);
    const [subjectApiResult, setSubjectApiResult] = useState([]);
    const [highlightedFields, setHightlightedFields] = useState<IFilterOptions>();
    const [workgroupOptions, setWorkgroupOptions] = useState([]);

    const {
        listAPI,
        ticketSubjectAPI,
        ticketAssigneeAPI,
        ticketRequestorsAPI,
        createdForAPI,
        findByCategoryAPI,
        customFieldOptionsAPI,
        findWorkgroupByTagName,
    } = useApiHandler();
    const {
        controller: { ticketQueues, roleList, tagsList, customFields, tkmWorkflows },
        ticketCategory,
        customFieldOptions,
    } = useSelector((state: any) => state.ticketMgmt.ticketsList) || {};

    const { handleSubmit, watch, setValue, reset, register, formState } = advanceSearchForm;
    const formData = watch();
    const dispatch = useDispatch();

    const tagListOptions = tagsList?.map(tag => ({ value: tag.id, text: tag.name, ...tag })) || [];

    useEffect(() => {
        const updatedWorkgroupOptions =
            ticketQueues?.map(({ name, ...rest }) => ({
                key: name,
                value: name,
                name,
                ...rest,
            })) || [];
        setWorkgroupOptions(updatedWorkgroupOptions);
    }, [ticketQueues]);

    useEffect(() => {
        const ticketListSearchVarsV1 = sessionStorage.getItem('ticketListSearchVarsV1');
        const isReportPageCall =
            document.referrer.indexOf(pendingReport) > 0 || parseQueryString().fromAgeingReports === 'true';
        const updatedValues = {};
        if (ticketListSearchVarsV1 !== 'undefined') {
            let parsedSearchCriteria = ticketListSearchVarsV1 ? JSON.parse(ticketListSearchVarsV1) : {};
            if (parsedSearchCriteria) {
                Object.keys(parsedSearchCriteria).forEach((key: any) => {
                    let value = parsedSearchCriteria[key];
                    if (['submittedBefore', 'submittedAfter'].includes(key)) {
                        value = parsedSearchCriteria[key] ? new Date(parsedSearchCriteria[key]) : '';
                    } else if (['ticketType', 'ticketStatus', 'ticketQueues', 'ticketPriority'].includes(key)) {
                        value = parsedSearchCriteria[key] !== '' ? parsedSearchCriteria[key].split(',') : [];
                        if (key === 'ticketType') {
                            value = [];
                        }
                    }
                    setValue(key, value);
                    updatedValues[key] = value;
                });
                setHightlightedFields(highlightFields(updatedValues));
                sessionStorage.setItem(
                    'ticketListSearchVarsV1',
                    JSON.stringify({ ...parsedSearchCriteria, isReportPageCall }),
                );
            }
        } else {
            sessionStorage.setItem(
                'ticketListSearchVarsV1',
                JSON.stringify({ ...DEFAULT_PAYLOAD, isReportPageCall, isSimplifiedView: false }),
            );
        }
    }, []);

    useEffect(() => {
        return () => {
            setTicketsSessionData(formData);
        };
    }, [formData]);

    const fetchList = useCallback(
        async (formData: any) => {
            const payload = setTicketsSessionData(formData);
            setHightlightedFields(highlightFields(formData));
            await listAPI(dispatch, { ...DEFAULT_PAYLOAD, ...payload });
        },
        [pageSortInfo],
    );

    const handleTicketCategoryChange = async (value: any) => {
        await findByCategoryAPI(dispatch, {
            category: value,
        });
        handleChange('ticketCategorySelect', value, setValue);
    };

    const handleWorkgroupChange = async (value: any) => {
        const tagName = tagsList.find((tag: { id: number }) => tag.id === Number(value))?.name;
        const { data, statusCode } = await findWorkgroupByTagName(dispatch, {
            tagName,
        });
        let updatedWorkgroupOptions = [];
        if (statusCode === API_STATUS_CODES.OK) {
            updatedWorkgroupOptions = data.map((queue: { name: string }) => ({
                key: queue.name,
                value: queue.name,
                ...queue,
            }));
        }
        setWorkgroupOptions(updatedWorkgroupOptions);
        handleChange('workGroupBytagId', value, setValue);
    };

    const onCancel = useCallback(() => {
        sessionStorage.setItem('ticketListSearchVarsV1', '{}');
        setHightlightedFields({} as IFilterOptions);
        reset();
        setResetTrigger(prev => !prev);
    }, [reset]);

    const getCustomField = (value: customFieldTypes) =>
        customFields.find((customField: { cfieldParamName: string }) => customField.cfieldParamName === value);

    const handleCustomFieldChange = async (value: customFieldTypes) => {
        if (getCustomField(value)?.cfieldType === customFieldTypes.OPTION_LIST) {
            await customFieldOptionsAPI(dispatch, { id: getCustomField(value)?.cfieldParamName });
        }
        setValue('customFieldSelectVal', '');
        handleChange('customFieldSelect', value, setValue);
    };

    const isCustomTextInput =
        getCustomField(formData.customFieldSelect as customFieldTypes)?.cfieldType === customFieldTypes.TEXT_BOX;
    const isCustomDateInput =
        getCustomField(formData.customFieldSelect as customFieldTypes)?.cfieldType === customFieldTypes.DATE_INPUT;
    const isCustomOptionInput =
        getCustomField(formData.customFieldSelect as customFieldTypes)?.cfieldType === customFieldTypes.OPTION_LIST;

    const fetchTicketSubject = async (subject: string) => {
        if (subjectApiResult.length === 0) {
            const { data } = await ticketSubjectAPI(dispatch, { csrftoken: window.csrf_token });
            setSubjectApiResult(data.map((item: string) => ({ id: item, label: item })));
        } else {
            return subjectApiResult.filter((item: any) => item.label.toLowerCase().includes(subject.toLowerCase()));
        }
    };

    const fetchAssignee = async (assignee: string) => {
        const { data } = await ticketAssigneeAPI(dispatch, {
            csrftoken: window.csrf_token,
            assigneeName: assignee,
        });
        return Object.entries(data).map(([key, value]: [string, string]) => ({ id: key, label: value }));
    };

    const fetchTicketRequestors = async (requestor: string) => {
        const { data } = await ticketRequestorsAPI(dispatch, {
            userText: requestor,
            csrftoken: window.csrf_token,
        });
        return Object.entries(data).map(([key, value]: [string, string]) => ({ id: key, label: value }));
    };

    const fetchCreatedFor = async (createdFor: string) => {
        const { data } = await createdForAPI(dispatch, {
            csrftoken: window.csrf_token,
            roleId: formData.createdForRole,
            moduleName: roleList?.find((role: { id: string }) => role.id == formData.createdForRole)?.label,
            userText: createdFor,
        });
        return Object.entries(data).map(([key, value]: [string, string]) => ({ id: key, label: value }));
    };

    const setSelectedItem = (value: Array<any>, labelFieldName: string, idFieldName?: string) => {
        handleChange(labelFieldName, value[0]?.label, setValue);
        if (idFieldName) {
            handleChange(idFieldName, value[0]?.id, setValue);
        }
    };

    return {
        handleSubmit,
        watch,
        setValue,
        reset,
        register,
        formState,
        onSubmit: fetchList,
        fetchTicketSubject,
        workgroupOptions,
        fetchAssignee,
        fetchTicketRequestors,
        roleList,
        tagsList,
        customFields,
        tkmWorkflows,
        fetchCreatedFor,
        onCancel,
        handleTicketCategoryChange,
        ticketCategory,
        customFieldOptions,
        getCustomField,
        handleCustomFieldChange,
        isCustomTextInput,
        isCustomDateInput,
        isCustomOptionInput,
        tagListOptions,
        values: formData,
        resetTrigger,
        setSelectedItem,
        highlightedFields,
        handleWorkgroupChange,
    };
};
